import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketPassSafeQuery, type TicketPasswordEntry } from '../api/easeedesk'
import { ApiError } from '../api/client'
import { revealPassword } from '../api/settingsPassSafe'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import './TicketPassSafe.css'

/**
 * Fila "Pass Safe" - `settings/pass_safe/reveal_pass.php`.
 *
 * TREI GRUPURI, ca in portal: parolele scrise pe tichet, cele ale omului
 * afectat, si cele ale aparatelor clientului pe tipuri.
 *
 * SE ARATA CE EXISTA, NU CE SCRIE IN EL. Raspunsul nu poarta nicio parola -
 * nici macar criptata - fiindca dezvaluirea are in portal poarta ei (nivelul
 * omului in firma SI drepturi pe parola anume) si lasa urma in jurnal
 * (`reveal_password`). Un al doilea drum catre acelasi secret ar fi insemnat sau
 * doua jurnale de citit impreuna, sau unul care nu prinde tot.
 *
 * Butonul cere dezvaluirea pe ACELASI drum ca Settings -> Pass Safe
 * (`POST /settings/pass_safe/passwords/{id}/reveal`): aceeasi poarta (dreptul pe
 * ecran si drepturile pe parola anume, verificate pe server) si acelasi rand in
 * jurnalul seifului. Adresa veche, `/easeedesk/passSafe/:id`, nu mai exista.
 */

export function TicketPassSafe({ id }: { id: number }) {
  const [type, setType] = useState(0)
  const { data, error } = useQuery(ticketPassSafeQuery(id, type))
  const [group, setGroup] = useState('')

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const groups = [
    { key: 'ticket', label: 'Ticket', rows: data.ticketPasswords },
    { key: 'user', label: 'User', rows: data.ownerPasswords },
    { key: 'devices', label: 'Devices', rows: data.devicePasswords },
    /* "Devices" ramane si gol, ca in portal: acolo e alegerea de tip, si fara
       ea n-ai de unde sti ca exista si alte tipuri. Celelalte doua se ascund
       cand n-au nimic - o fila goala nu spune nimic despre tichet. */
  ].filter((one) => one.key === 'devices' || one.rows.length > 0)

  const open = groups.some((one) => one.key === group) ? group : groups[0].key
  const shown = groups.find((one) => one.key === open)

  return (
    <div id={`pass-safe-${id}`}>
      <ul className="nav nav-pills pass-safe-pills ps-pills">
        {groups.map((one) => (
          <li key={one.key} role="presentation" className={open === one.key ? 'active' : undefined}>
            <a onClick={() => setGroup(one.key)}>{one.label}</a>
          </li>
        ))}
      </ul>

      {open === 'devices' &&
        (data.deviceTypes.length === 0 ? (
          <div className="alert alert-info ps-note" role="alert">
            <i className="fas fa-info-circle" aria-hidden="true"></i> There are no passwords registered!
          </div>
        ) : (
          <div className="ps-pick">
            <label className="control-label" htmlFor={`ps-type-${id}`}>
              Device Type:
            </label>
            <select
              id={`ps-type-${id}`}
              className="form-control"
              value={data.deviceTypeId}
              onChange={(ev) => setType(Number(ev.target.value))}
            >
              {data.deviceTypes.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
          </div>
        ))}

      <Rows rows={shown?.rows ?? []} withDevice={open === 'devices'} />
    </div>
  )
}

function Rows({ rows, withDevice }: { rows: TicketPasswordEntry[]; withDevice: boolean }) {
  const [revealed, setRevealed] = useState<{ id: number; password: string } | null>(null)
  const [busy, setBusy] = useState(0)

  if (rows.length === 0) return null

  /* Acelasi gest ca in Settings -> Pass Safe: intai intrebarea, apoi un POST
     care trece prin poarta si se scrie in jurnal. */
  const reveal = async (id: number) => {
    if (!window.confirm('Are you sure that you want to reveal this password?')) return
    setBusy(id)
    try {
      const answer = await revealPassword(id)
      setRevealed({ id, password: answer.password })
    } catch (error) {
      toast(
        error instanceof ApiError ? error.message : 'You do not have rights to reveal this password.',
        'Error',
        'error',
      )
    } finally {
      setBusy(0)
    }
  }

  return (
    <>
      <table className="table table-hover ps-table">
        <thead>
          <tr>
            {withDevice && <th>Device</th>}
            <th>Description</th>
            <th>Username</th>
            <th>Password</th>
            <th className="ps-center">Reveal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {withDevice && <td>{row.device}</td>}
              <td>{row.description}</td>
              <td>{row.user}</td>
              {/* Nu e o parola ascunsa pe ecran: nu a venit niciuna. */}
              <td className="ps-mask">******</td>
              <td className="ps-center">
                <button
                  type="button"
                  className="btn btn-success btn-xs"
                  disabled={busy === row.id}
                  onClick={() => void reveal(row.id)}
                  title="Reveal this password. The reveal is written to the vault log."
                  aria-label={`Reveal password ${row.description}`}
                >
                  <i className="fa fa-eye" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        id="ticketRevealPasswordModal"
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        labelledBy="ticketRevealPasswordModalLabel"
        portal
        title={
          <>
            <i className="fa fa-eye" aria-hidden="true"></i> The password has been revealed
          </>
        }
      >
        <div className="ps-revealed">{revealed?.password}</div>
        <p className="text-muted" style={{ fontSize: 12 }}>
          This was written to the vault log, with your name and the time. Close the window when you are done.
        </p>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-default"
            onClick={() => {
              if (revealed) void navigator.clipboard?.writeText(revealed.password)
              toast('Password copied.', 'Copied', 'success')
            }}
          >
            Copy
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setRevealed(null)}>
            Close
          </button>
        </div>
      </Modal>
    </>
  )
}
